/**
 * RunHistoryTable — sortable Architect run list.
 *
 * Plain <table> per ADR-057: no decorative chrome, hairline borders,
 * hover-bg only. Status badge uses the status taxonomy palette.
 */
import type { ArchitectRunSummary } from '@chaos/types/architect';
import { Link } from '@tanstack/react-router';
import * as React from 'react';

import { cn } from '../../lib/utils';

interface RunHistoryTableProps {
  runs: ArchitectRunSummary[];
}

function formatDuration(start: string, end: string | null): string {
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return '—';
  const delta = Math.max(0, endMs - startMs);
  if (delta < 60_000) return `${(delta / 1_000).toFixed(1)}s`;
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m`;
  const h = Math.floor(delta / 3_600_000);
  const m = Math.floor((delta % 3_600_000) / 60_000);
  return `${h}h ${m}m`;
}

function statusChipClass(status: ArchitectRunSummary['status']): string {
  switch (status) {
    case 'succeeded':
      return 'bg-success/15 text-success';
    case 'running':
      return 'bg-foreground/8 text-foreground';
    case 'failed':
      return 'bg-error/15 text-error';
    case 'cancelled':
      return 'bg-warning/15 text-warning';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function truncate(text: string, limit: number): string {
  if (text.length <= limit) return text;
  return `${text.slice(0, limit - 1).trimEnd()}…`;
}

type SortKey = 'startedAt' | 'duration' | 'cardCount';
type SortDirection = 'asc' | 'desc';

export function RunHistoryTable({ runs }: RunHistoryTableProps): React.JSX.Element {
  const [sortKey, setSortKey] = React.useState<SortKey>('startedAt');
  const [direction, setDirection] = React.useState<SortDirection>('desc');

  const sorted = React.useMemo(() => {
    const sign = direction === 'asc' ? 1 : -1;
    return [...runs].sort((a, b) => {
      if (sortKey === 'startedAt') {
        return sign * (new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime());
      }
      if (sortKey === 'cardCount') {
        return sign * (a.cardCount - b.cardCount);
      }
      const aDur =
        (a.endedAt ? new Date(a.endedAt).getTime() : Date.now()) - new Date(a.startedAt).getTime();
      const bDur =
        (b.endedAt ? new Date(b.endedAt).getTime() : Date.now()) - new Date(b.startedAt).getTime();
      return sign * (aDur - bDur);
    });
  }, [runs, sortKey, direction]);

  const toggleSort = (key: SortKey): void => {
    if (sortKey === key) {
      setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setDirection('desc');
    }
  };

  if (runs.length === 0) {
    return (
      <div className="rounded border border-border px-4 py-8 text-center text-[12px] text-muted-foreground/70">
        No runs yet. Use “+ New task” to dispatch one.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded border border-border">
      <table className="w-full text-[13px]">
        <thead className="bg-muted/30 text-[11px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <SortHeader
              active={sortKey === 'startedAt'}
              direction={direction}
              onClick={() => toggleSort('startedAt')}
              className="w-[160px]"
            >
              Started
            </SortHeader>
            <th className="px-3 py-2 text-left font-medium">Description</th>
            <th className="w-[120px] px-3 py-2 text-left font-medium">Status</th>
            <SortHeader
              active={sortKey === 'duration'}
              direction={direction}
              onClick={() => toggleSort('duration')}
              className="w-[100px]"
            >
              Duration
            </SortHeader>
            <SortHeader
              active={sortKey === 'cardCount'}
              direction={direction}
              onClick={() => toggleSort('cardCount')}
              className="w-[80px]"
            >
              Cards
            </SortHeader>
          </tr>
        </thead>
        <tbody>
          {sorted.map((run) => (
            <tr
              key={run.id}
              className="border-t border-border transition-colors hover:bg-foreground/5"
            >
              <td className="px-3 py-2 text-foreground">
                <Link
                  to="/architect/run/$runId"
                  params={{ runId: run.id }}
                  className="hover:underline"
                >
                  {new Date(run.startedAt).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Link>
              </td>
              <td className="px-3 py-2 text-foreground">
                <Link
                  to="/architect/run/$runId"
                  params={{ runId: run.id }}
                  className="hover:underline"
                >
                  {truncate(run.description, 80)}
                </Link>
                {run.dryRun && (
                  <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    dry-run
                  </span>
                )}
              </td>
              <td className="px-3 py-2">
                <span
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                    statusChipClass(run.status),
                  )}
                >
                  {run.status}
                </span>
              </td>
              <td className="px-3 py-2 text-muted-foreground">
                {formatDuration(run.startedAt, run.endedAt)}
              </td>
              <td className="px-3 py-2 text-muted-foreground">{run.cardCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface SortHeaderProps {
  active: boolean;
  direction: SortDirection;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}

function SortHeader({
  active,
  direction,
  onClick,
  className,
  children,
}: SortHeaderProps): React.JSX.Element {
  return (
    <th scope="col" className={cn('px-3 py-2 text-left font-medium', className)}>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex items-center gap-1 text-[11px] uppercase tracking-wider transition-colors',
          active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
        )}
      >
        <span>{children}</span>
        {active && <span aria-hidden>{direction === 'asc' ? '↑' : '↓'}</span>}
      </button>
    </th>
  );
}
